import type { PinduoduoMasterVariant } from './platform'
import { pinduoduoCostParametersOn } from './pinduoduoProfit'
import type { CompanyProductOverrideRecord, InternalProductInfoRecord, StoreCostProfile } from './types'

/**
 * 拼多多单件利润测算：参数默认取当前生效店铺成本版本与公司产品成本，
 * 全部字段允许本页调整；公式与 estimatePinduoduoProfit 的单件口径完全一致，
 * 由 verify-pinduoduo-profit-calculator.mjs 做逐项parity回归。
 */

export interface CalculatorInputs {
  price: number
  productCost: number
  packagingCost: number
  giftCost: number
  shippingInsuranceCost: number
  shippingCost: number
  platformFeeRatePercent: number
  taxRatePercent: number
  rebateRatePercent: number
}

export interface CalculatorDefaults {
  inputs: CalculatorInputs
  isAccessory: boolean
  isBrandSpecial: boolean
  hasProductCost: boolean
  hasStoreCost: boolean
  costIssue: string | null
  storeCostEffectiveDate: string
}

export interface CalculatorResult {
  fulfillmentCost: number
  rateFee: number
  platformFee: number
  taxFee: number
  totalCost: number
  profit: number
  rebateAmount: number
  profitWithRebate: number
  profitRate: number | null
  profitWithRebateRate: number | null
  breakEvenRoi: number | null
}

export function calculatorDefaultsForVariant(
  variant: PinduoduoMasterVariant,
  profile: StoreCostProfile | undefined,
  companyRecords: InternalProductInfoRecord[],
  companyOverride: CompanyProductOverrideRecord | undefined,
  date: string,
): CalculatorDefaults {
  const parameters = pinduoduoCostParametersOn(profile, companyRecords, companyOverride, date)
  const { storeCost, productCost } = parameters
  return {
    inputs: {
      price: variant.groupPrice ?? variant.singlePrice ?? 0,
      productCost: productCost.productCost ?? 0,
      packagingCost: storeCost?.packagingCost ?? 0,
      giftCost: parameters.giftCost,
      shippingInsuranceCost: storeCost?.shippingInsuranceFee ?? 0,
      shippingCost: storeCost?.shippingFee ?? 0,
      platformFeeRatePercent: storeCost?.platformFeeRate ?? 0,
      taxRatePercent: storeCost?.taxRate ?? 0,
      rebateRatePercent: parameters.rebateRate === null ? 0 : parameters.rebateRate * 100,
    },
    isAccessory: parameters.accessory,
    isBrandSpecial: productCost.isBrandSpecial,
    hasProductCost: productCost.productCost !== null,
    hasStoreCost: Boolean(storeCost),
    costIssue: productCost.issue,
    storeCostEffectiveDate: storeCost?.effectiveDate ?? '',
  }
}

export function computePinduoduoUnitProfit(inputs: CalculatorInputs): CalculatorResult {
  const fulfillmentCost = inputs.productCost + inputs.packagingCost + inputs.giftCost
    + inputs.shippingInsuranceCost + inputs.shippingCost
  const platformFee = inputs.price * inputs.platformFeeRatePercent / 100
  const taxFee = inputs.price * inputs.taxRatePercent / 100
  const rateFee = platformFee + taxFee
  const profit = inputs.price - fulfillmentCost - rateFee
  const rebateAmount = inputs.productCost * inputs.rebateRatePercent / 100
  const profitWithRebate = profit + rebateAmount
  return {
    fulfillmentCost,
    rateFee,
    platformFee,
    taxFee,
    totalCost: fulfillmentCost + rateFee,
    profit,
    rebateAmount,
    profitWithRebate,
    profitRate: inputs.price > 0 ? profit / inputs.price : null,
    profitWithRebateRate: inputs.price > 0 ? profitWithRebate / inputs.price : null,
    breakEvenRoi: inputs.price > 0 && profitWithRebate > 0 ? inputs.price / profitWithRebate : null,
  }
}

export interface AdSimulationResult {
  profitAfterAdvertising: number
  impliedRoi: number | null
}

export function simulateAdvertising(result: CalculatorResult, price: number, adSpendPerUnit: number): AdSimulationResult {
  return {
    profitAfterAdvertising: result.profitWithRebate - adSpendPerUnit,
    impliedRoi: adSpendPerUnit > 0 ? price / adSpendPerUnit : null,
  }
}

/**
 * 退货售后成本模拟：沿用店铺二退损失口径（D-146）——
 * 单件二退损失＝往返运费＋包装损耗＋产品成本×货损率；
 * 按支付队列二退率计算每下单件的期望损失与期望利润。
 */
export interface AfterSalesInputs {
  secondReturnRatePercent: number
  returnShippingCost: number
  packagingLossCost: number
  damageRatePercent: number
}

export interface AfterSalesResult {
  unitSecondReturnLoss: number
  expectedLossPerOrdered: number
  profitPerCompleted: number
  expectedProfitForegonePerOrdered: number
  afterSalesProfitAdjustmentPerOrdered: number
  expectedProfitPerOrdered: number
  revenuePerCompleted: number
  expectedRevenuePerOrdered: number
  afterSalesBreakEvenRoi: number | null
}

export interface PinduoduoCashRecoveryResult {
  platformRemittancePerCompleted: number
  netCashRecoveryPerCompleted: number
  expectedNetCashRecoveryPerOrdered: number
  expectedCashRecoveryPerClearedUnit: number | null
  clearedUnitBreakdown: {
    price: number
    platformFee: number
    taxFee: number
    packagingCost: number
    giftCost: number
    shippingInsuranceCost: number
    initialShippingCost: number
    expectedAdvertisingCost: number
    expectedRetryCashCost: number
  }
  estimatedInventoryCashRecovery: number | null
}

/**
 * 清仓变现口径：
 * - 平台打款只扣平台直接扣除的扣点；
 * - 净变现继续扣广告、税费与本次销售触发的履约/二退现金支出；
 * - 产品成本是库存形成时已经投入的沉没成本，不从本次现金变现中再次扣除。
 */
export function computePinduoduoCashRecovery(
  result: CalculatorResult,
  inputs: CalculatorInputs,
  afterSales: AfterSalesResult,
  afterSalesInputs: AfterSalesInputs,
  adSpendPerUnit: number,
  inventoryQuantity: number | null = null,
): PinduoduoCashRecoveryResult {
  const platformRemittancePerCompleted = afterSales.revenuePerCompleted - result.platformFee
  const secondReturnRate = Math.min(100, Math.max(0, afterSalesInputs.secondReturnRatePercent)) / 100
  const completionRate = 1 - secondReturnRate
  const nonInventoryFulfillmentCost = result.fulfillmentCost - inputs.productCost
  const cashRecoveryBeforeAdvertisingPerCompleted = platformRemittancePerCompleted
    - result.taxFee
    - nonInventoryFulfillmentCost
  const cashAfterSalesLossPerReturn = afterSalesInputs.returnShippingCost
    + afterSalesInputs.packagingLossCost
  const expectedNetCashRecoveryPerOrdered = completionRate * cashRecoveryBeforeAdvertisingPerCompleted
    - (1 - completionRate) * cashAfterSalesLossPerReturn
    - adSpendPerUnit
  const hasInventoryQuantity = inventoryQuantity !== null
    && Number.isFinite(inventoryQuantity)
    && inventoryQuantity >= 0
  const expectedCashRecoveryPerClearedUnit = completionRate > 0
    ? expectedNetCashRecoveryPerOrdered / completionRate
    : null
  const expectedAdvertisingCost = completionRate > 0 ? adSpendPerUnit / completionRate : 0
  const expectedRetryCashCost = completionRate > 0
    ? secondReturnRate / completionRate * cashAfterSalesLossPerReturn
    : 0
  return {
    platformRemittancePerCompleted,
    netCashRecoveryPerCompleted: cashRecoveryBeforeAdvertisingPerCompleted - adSpendPerUnit,
    expectedNetCashRecoveryPerOrdered,
    expectedCashRecoveryPerClearedUnit,
    clearedUnitBreakdown: {
      price: inputs.price,
      platformFee: result.platformFee,
      taxFee: result.taxFee,
      packagingCost: inputs.packagingCost,
      giftCost: inputs.giftCost,
      shippingInsuranceCost: inputs.shippingInsuranceCost,
      initialShippingCost: inputs.shippingCost,
      expectedAdvertisingCost,
      expectedRetryCashCost,
    },
    estimatedInventoryCashRecovery: hasInventoryQuantity && expectedCashRecoveryPerClearedUnit !== null
      ? expectedCashRecoveryPerClearedUnit * inventoryQuantity
      : null,
  }
}

export function afterSalesDefaultsFor(inputs: CalculatorInputs, secondReturnRatePercent: number): AfterSalesInputs {
  return {
    secondReturnRatePercent,
    returnShippingCost: inputs.shippingCost * 2,
    packagingLossCost: inputs.packagingCost,
    damageRatePercent: 5,
  }
}

/**
 * 广告投放模拟（按每下单1件）：广告费发生在下单前，因此在退货售后期望成本之后扣除；
 * 对应投产比＝期望成交额÷单件广告费，可与平台归因投产比直接对比。
 */
export function simulateAdvertisingAfterAfterSales(
  afterSales: AfterSalesResult,
  adSpendPerUnit: number,
): AdSimulationResult {
  return {
    profitAfterAdvertising: afterSales.expectedProfitPerOrdered - adSpendPerUnit,
    impliedRoi: adSpendPerUnit > 0 ? afterSales.expectedRevenuePerOrdered / adSpendPerUnit : null,
  }
}

/**
 * 含CPS佣金的广告模拟（京挑客）：CPS＝渠道券后的成交价×佣金率，只对成交计佣（退货退佣）。
 * 渠道券可让CPS单使用独立的券后利润，直投和自然单继续使用原利润。
 */
export interface JdChannelSimulationResult {
  organicProfitPerCompleted: number
  organicAfterSalesAdjustmentPerOrdered: number
  organicProfitPerOrdered: number
  directAdProfitPerCompleted: number
  directAdAfterSalesAdjustmentPerOrdered: number
  directAdProfitPerOrdered: number
  // 保本直投投产比：期望成交额 ÷ 考虑售后的期望利润；京准通实际投产比低于该值即亏损
  breakEvenDirectRoi: number | null
  cpsCommissionPerCompleted: number
  expectedCpsPerOrdered: number
  cpsProfitPerCompleted: number
  cpsAfterSalesAdjustmentPerOrdered: number
  cpsProfitPerOrdered: number
  // 保本CPS佣金率（%）：佣金率高于该值则CPS单亏损
  breakEvenCpsRatePercent: number | null
}

export function jdCpsCouponDiscount(
  customerPrice: number,
  amount: number,
) {
  if (!Number.isFinite(amount) || amount <= 0) return 0
  return Math.min(amount, Math.max(customerPrice, 0))
}

// 京东广告渠道按结算互斥：直投（京准通）按点击扣费、其归因单不付CPS；
// 京挑客CPS按成交计佣、退货退佣；自然流量单两者皆不付。
// 因此按渠道分别测算单件利润，不在同一件上同时扣直投费与CPS佣金。
export function simulateJdAdvertisingChannels(
  afterSales: AfterSalesResult,
  adSpendPerUnit: number,
  cpsRatePercent: number,
  cpsBaseRevenuePerOrdered: number = afterSales.expectedRevenuePerOrdered,
  cpsProfitBeforeCommissionPerOrdered: number = afterSales.expectedProfitPerOrdered,
  cpsBaseRevenuePerCompleted: number = afterSales.revenuePerCompleted,
  cpsProfitBeforeCommissionPerCompleted: number = afterSales.profitPerCompleted,
): JdChannelSimulationResult {
  const profit = afterSales.expectedProfitPerOrdered
  const organicProfitPerCompleted = afterSales.profitPerCompleted
  const directAdProfitPerCompleted = organicProfitPerCompleted - adSpendPerUnit
  const directAdProfitPerOrdered = profit - adSpendPerUnit
  const cpsCommissionPerCompleted = cpsBaseRevenuePerCompleted * cpsRatePercent / 100
  const expectedCpsPerOrdered = cpsBaseRevenuePerOrdered * cpsRatePercent / 100
  const cpsProfitPerCompleted = cpsProfitBeforeCommissionPerCompleted - cpsCommissionPerCompleted
  const cpsProfitPerOrdered = cpsProfitBeforeCommissionPerOrdered - expectedCpsPerOrdered
  return {
    organicProfitPerCompleted,
    organicAfterSalesAdjustmentPerOrdered: profit - organicProfitPerCompleted,
    organicProfitPerOrdered: profit,
    directAdProfitPerCompleted,
    directAdAfterSalesAdjustmentPerOrdered: directAdProfitPerOrdered - directAdProfitPerCompleted,
    directAdProfitPerOrdered,
    breakEvenDirectRoi: afterSales.expectedRevenuePerOrdered > 0 && profit > 0
      ? afterSales.expectedRevenuePerOrdered / profit
      : null,
    cpsCommissionPerCompleted,
    expectedCpsPerOrdered,
    cpsProfitPerCompleted,
    cpsAfterSalesAdjustmentPerOrdered: cpsProfitPerOrdered - cpsProfitPerCompleted,
    cpsProfitPerOrdered,
    breakEvenCpsRatePercent: cpsBaseRevenuePerOrdered > 0 && cpsProfitBeforeCommissionPerOrdered > 0
      ? cpsProfitBeforeCommissionPerOrdered / cpsBaseRevenuePerOrdered * 100
      : null,
  }
}

export function computeAfterSalesAdjusted(
  result: CalculatorResult,
  inputs: CalculatorInputs,
  afterSales: AfterSalesInputs,
): AfterSalesResult {
  const unitSecondReturnLoss = afterSales.returnShippingCost + afterSales.packagingLossCost
    + inputs.productCost * afterSales.damageRatePercent / 100
  const rate = afterSales.secondReturnRatePercent / 100
  const expectedLossPerOrdered = rate * unitSecondReturnLoss
  const profitPerCompleted = result.profitWithRebate
  const expectedProfitForegonePerOrdered = rate * profitPerCompleted
  const expectedProfitPerOrdered = (1 - rate) * result.profitWithRebate - expectedLossPerOrdered
  const expectedRevenuePerOrdered = (1 - rate) * inputs.price
  return {
    unitSecondReturnLoss,
    expectedLossPerOrdered,
    profitPerCompleted,
    expectedProfitForegonePerOrdered,
    afterSalesProfitAdjustmentPerOrdered: expectedProfitPerOrdered - profitPerCompleted,
    expectedProfitPerOrdered,
    revenuePerCompleted: inputs.price,
    expectedRevenuePerOrdered,
    afterSalesBreakEvenRoi: expectedRevenuePerOrdered > 0 && expectedProfitPerOrdered > 0
      ? expectedRevenuePerOrdered / expectedProfitPerOrdered
      : null,
  }
}
